import React, { useState, useEffect } from "react";
import { useApp } from "context";
import { Custom, Form } from "components";
import { Enums, Constants } from "utils";
import { IClinic, IGoal, IProvider, IQarePlan, IService } from "interfaces";
import { Feedback, PairValue } from "types";

type Props = {
  sx?: any;
  opened: boolean;
  clinic?: IClinic;
  service: IService;
  provider?: IProvider;
  qarePlan?: IQarePlan;
  onBack?: () => void;
  onClose?: (updated: boolean) => void;
};

// TODO: reset the modal props when the modal is closed
export const MakeAppointment = (props: Props) => {
  const { t, service, setFeedback } = useApp();

  const [clinics, setClinics] = useState([]);
  const [reasons, setReasons] = useState([]);
  const [healthGoals, setHealthGoals] = useState([]);
  const [message, setMessage] = useState("");

  useEffect(() => {
    handleAppointmentChange();
  }, [props.opened]);

  const isWalkIn = false;

  const serviceId = isWalkIn
    ? Enums.EnumService.WalkInClinic
    : props.service.serviceId;

  const clinicId: number | "" = props.clinic?.clinicId ?? "";

  const serviceName = isWalkIn
    ? t.title.patient.walk_in
    : props.service.serviceName;

  const enumSource = Constants.serviceMap.get(serviceId)?.enumSource ?? {};

  const findClinics = async () => {
    if (!serviceId) return;
    const providerId = props.provider?.providerId;
    const response = providerId
      ? await service.clinic.findByProviderAndService(
          providerId ?? -1,
          serviceId
        )
      : await service.clinic.findByService(serviceId);
    setClinics(response);
  };

  const findReasons = () => {
    const reasons = (Object.entries(enumSource) as any).map(
      (values: any, i: number) =>
        ({ key: i, value: t.reason[values[1]] } as PairValue)
    );
    setReasons(reasons);
  };

  const findHealthGoals = async () => {
    const [status, response] = await service.goal.all();
    const healthGoals = response?.map(
      (item: IGoal) =>
        ({ key: item.healthGoalId, value: item.healthGoal } as PairValue)
    );
    setHealthGoals(healthGoals);
  };

  const handleBack = () => props.onBack && props.onBack();

  const handleAppointmentDone = (updated: boolean) => {
    if (updated) {
      setFeedback({
        message: t.message.appointment.appointment_success,
        severity: Enums.EnumFeedback.Success,
      } as Feedback);
    }
    props.onClose && props.onClose(updated);
  };

  const handleAppointmentChange = () => {
    setClinics([]);
    setReasons([]);
    setHealthGoals([]);
    setMessage("");
    handleAppointmentLoad();
  };

  const handleAppointmentError = (message?: string) => {
    setMessage(message ?? t.message.appointment.appointment_error);
  };

  const handleAppointmentLoad = () => {
    findClinics();
    findReasons();
    findHealthGoals();
  };

  return (
    <Custom.Modal
      onBack={handleBack}
      message={message}
      header={t.action.make_appointment}
      subheader={serviceName}
      opened={props.opened || isWalkIn}
      onOpen={handleAppointmentLoad}
      onClose={() => handleAppointmentDone(false)}
    >
      <Form.Appointment
        isWalkIn={isWalkIn}
        services={enumSource}
        serviceId={serviceId}
        service={props.service}
        clinicId={clinicId}
        healthGoals={healthGoals}
        clinics={clinics}
        reasons={reasons}
        qarePlan={props.qarePlan}
        onDone={handleAppointmentDone}
        onError={handleAppointmentError}
      />
    </Custom.Modal>
  );
};
